import React from 'react'
import { Box, Flex, Text } from '@workwhile/ui'

export type PayBreakdown = {
  wallClockDurationMinutes: number
  minPayLengthMinutes: number
  unpaidLunchBreakLengthMinutes: number
  totalWorkDurationMinutes: number
}

function formatMinutes(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) {
    return '—'
  }

  const wholeMinutes = Math.round(minutes)
  const hours = Math.floor(wholeMinutes / 60)
  const remainingMinutes = wholeMinutes % 60
  if (hours === 0) {
    return `${remainingMinutes}m`
  }
  if (remainingMinutes === 0) {
    return `${hours}h`
  }
  return `${hours}h ${remainingMinutes}m`
}

function formatDecimalHours(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) {
    return '—'
  }
  return `${(minutes / 60).toFixed(2)}h`
}

function formatDuration(minutes: number): string {
  return `${formatMinutes(minutes)} (${formatDecimalHours(minutes)})`
}

type PayBreakdownRowProps = {
  label: string
  minutes: number
  sign?: '+' | '−'
}

const PayBreakdownRow = ({ label, minutes, sign }: PayBreakdownRowProps) => (
  <Flex justifyContent="space-between" alignItems="center" gap={3}>
    <Text fontSize={1} color="lightText">
      {label}
    </Text>
    <Text fontSize={1} fontWeight={600} style={{ whiteSpace: 'nowrap' }}>
      {sign != null ? `${sign} ` : null}
      {formatDuration(minutes)}
    </Text>
  </Flex>
)

type PayBreakdownTooltipContentProps = {
  payBreakdown: PayBreakdown
}

export const PayBreakdownTooltipContent = ({
  payBreakdown,
}: PayBreakdownTooltipContentProps) => (
  <Flex flexDirection="column" gap={1} style={{ minWidth: 180 }}>
    <Text fontSize="sm" fontWeight="bold" mb={1}>
      Pay breakdown
    </Text>
    <PayBreakdownRow
      label="Wall clock"
      minutes={payBreakdown.wallClockDurationMinutes}
    />
    {payBreakdown.minPayLengthMinutes > 0 ? (
      <PayBreakdownRow
        label="Min pay"
        minutes={payBreakdown.minPayLengthMinutes}
        sign="+"
      />
    ) : null}
    {payBreakdown.unpaidLunchBreakLengthMinutes > 0 ? (
      <PayBreakdownRow
        label="Unpaid lunch"
        minutes={payBreakdown.unpaidLunchBreakLengthMinutes}
        sign="−"
      />
    ) : null}
    <Box pt={1} mt={1} borderTop="1px solid" borderColor="neutrals.100">
      <Flex justifyContent="space-between" alignItems="center" gap={3}>
        <Text fontSize={1} fontWeight="bold">
          Payable
        </Text>
        <Text fontSize={1} fontWeight="bold" style={{ whiteSpace: 'nowrap' }}>
          {formatDuration(payBreakdown.totalWorkDurationMinutes)}
        </Text>
      </Flex>
    </Box>
  </Flex>
)

export { formatMinutes as formatPayBreakdownMinutes }
